import { MultiSelectOption } from "./IMultiSelectOption";

/*
 * Utility functions for marshalling GraphQL list data into a nested form that
 * can be used as @KazooHR/Confetti MultiSelect items.
 */
type MapNodeItem = Omit<MultiSelectOption, "parent"> & {
  parent?: { id?: string };
};
export function mapNodes(items: MapNodeItem[] = []): MultiSelectOption[] {
  return items.map((n) => ({
    ...n,
    parent: n?.parent?.id,
    children: [],
  }));
}

export function flatArrayToTree(list: MultiSelectOption[]) {
  const treeList: MultiSelectOption[] = [];
  const lookup: any = {};
  /* Creating a new object with each "obj.id" as "key" */
  list.forEach((obj: MultiSelectOption) => {
    const x: string = obj.id;
    lookup[x] = { ...obj };
  });
  /* Looping the list - setting as children those who have a parent  */
  list.forEach((obj: MultiSelectOption) => {
    const x: string = obj.id;
    const current: MultiSelectOption = lookup[x];
    if (obj.parent !== undefined && lookup[obj.parent] !== undefined) {
      const y: string = obj.parent;
      lookup[y].children.push(current);
    } else {
      /* If it does not have a parent, he's a 1st level node */
      treeList.push(current);
    }
  });
  return treeList;
}

/**
 * Takes in a list of items with id and parent.id properties and constructs a
 * tree of items for use in @kazoohr/Confetti MultiSelect.  The nested objects
 * reserve and overwite the props "parent" and "children".
 *
 * Example input:
 * [
 *   {
 *     parent: { id: "foo" },
 *     id: "bar",
 *     someRandomProp: 1437,
 *     children: "will get overwritten"
 *   },
 *   {
 *     parent: null/undefined/whatever,
 *     id: "foo",
 *     someRandomProp: "will get preserved"
 *   },
 *   {
 *     id: "baz",
 *     anotherRandomProp: "also preserved"
 *   }
 * ]
 *
 * Example output:
 * [
 *   {
 *     id: "foo",
 *     someRandomProp: "will get preserved",
 *     parent: null,
 *     children: [
 *       {
 *         id: "bar",
 *         someRandomProp: 1437,
 *         parent: "foo",
 *         children: []
 *       }
 *     ]
 *   },
 *   {
 *     id: "baz",
 *     anotherRandomProp: "also preserved",
 *     parent: null,
 *     children: [],
 *   }
 * ]
 *
 */
export function flatArrayToMultiSelectTree(items: MapNodeItem[] = []) {
  return flatArrayToTree(mapNodes(items));
}
